import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Container,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import FadeInUp from "bundles/common/utils/FadeInUp";
import ContactForm from "bundles/Main/components/ContactForm";
import Image3 from "images/psychology-image3_2560.webp";
import MedicineIcon from "images/medicine.svg";
import HandshakeIcon from "images/handshake.svg";
import StrongIcon from "images/strong.svg";
import BrainIcon from "images/brain.svg";
import WorldIcon from "images/world.svg";
import MentorshipIcon from "images/mentorship.svg";
import ApplyButton from "buttons/apply-button-blue.svg";

const placements = [
  {
    icon: MedicineIcon,
    number: "01",
    title: "Clinical Placement – Regional Hospital",
    color: "#1565C0",
    lightColor: "#E3F2FD",
    description:
      "Join the psychology department of a regional referral hospital in Arusha. Participants observe and participate in consultations, case discussions, and multidisciplinary collaboration. Exposure may include mood disorders, trauma-related conditions, substance use challenges, and community mental health cases within a cross-cultural context. This placement focuses primarily on clinical observation and professional exchange, with supervised participation where appropriate.",
  },
  {
    icon: HandshakeIcon,
    number: "02",
    title: "Children & Youth Program",
    color: "#2E7D32",
    lightColor: "#E8F5E9",
    description:
      "Work alongside local professionals supporting children in orphanages and youth who have experienced social vulnerability, including children who live or have previously lived on the streets. Participants may help facilitate structured activities focused on emotional expression, confidence building, and social connection, creating safe and supportive spaces where children can learn, play, and develop a sense of stability and belonging.",
  },
  {
    icon: StrongIcon,
    number: "03",
    title: "Women's Empowerment Program",
    color: "#6A1B9A",
    lightColor: "#F3E5F5",
    description:
      "Support community-based initiatives that promote psychological well-being, confidence, and self-reliance among women. Participants may assist with group discussions, awareness sessions, and structured activities designed to strengthen emotional resilience, encourage open dialogue, and foster supportive community networks.",
  },
  {
    icon: BrainIcon,
    number: "04",
    title: "Substance Use Recovery Program",
    color: "#BF360C",
    lightColor: "#FBE9E7",
    description:
      "Support individuals working to rebuild their lives after addiction. Participants engage with community programs that assist people affected by substance use, helping create supportive and encouraging environments for recovery. Volunteers may observe counseling sessions, assist with group activities, and contribute to initiatives that promote emotional support, resilience, and social reintegration.",
  },
  {
    icon: WorldIcon,
    number: "05",
    title: "Psychoeducation & Community Outreach",
    color: "#00695C",
    lightColor: "#E0F2F1",
    description:
      "Collaborate with local professionals to support mental health awareness initiatives in schools, community centers, and local organizations. Participants may contribute to educational activities that help communities better understand mental health, reduce stigma, and encourage open and supportive conversations.",
  },
  {
    icon: MentorshipIcon,
    number: "06",
    title: "Workshop Development Project",
    color: "#37474F",
    lightColor: "#ECEFF1",
    description:
      "Design and facilitate a culturally adapted psychoeducational workshop in collaboration with local professionals. Participants help develop and implement educational content tailored to identified community needs, ensuring that workshops are relevant, accessible, and meaningful for the communities involved.",
  },
];

const PlacementCard = ({ icon, number, title, color, lightColor, description, isDesktop }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <FadeInUp style={{ width: "100%", display: "flex" }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          width: "100%",
          backgroundColor: "white",
          borderRadius: 3,
          boxShadow: hovered
            ? "0 12px 32px rgba(0,0,0,0.13)"
            : "0 2px 12px rgba(0,0,0,0.07)",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transform: hovered ? "translateY(-5px)" : "translateY(0)",
          transition: "all 0.35s ease",
        }}
      >
        <Box
          sx={{
            backgroundColor: lightColor,
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={icon}
              alt={title}
              style={{
                width: "52%",
                height: "auto",
                filter: "brightness(0) invert(1)",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: "800",
              fontSize: "13px",
              color: color,
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            {number}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: isDesktop ? "17px !important" : "16px !important",
              mb: 1.5,
              lineHeight: 1.35,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "15px !important",
              lineHeight: 1.75,
              color: "text.secondary",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </FadeInUp>
  );
};

const PsychologyPlacements = () => {
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [applyHovered, setApplyHovered] = useState(false);

  return (
    <Box>
      {/* ── Top nav strip ── */}
      <Box
        sx={{
          borderBottom: "1px solid #e8e8e8",
          px: isDesktop ? 10 : 3,
          py: 1.5,
          backgroundColor: "white",
        }}
      >
        <Link
          to={`/${i18nLocale}/career_fields/psychology`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px !important",
              color: "#555",
              fontStyle: "italic",
              transition: "color 0.2s ease",
              "&:hover": { color: "#1565C0" },
            }}
          >
            ← Psychology
          </Typography>
        </Link>
      </Box>

      {/* ── Intro: bold split layout ── */}
      <Box sx={{ backgroundColor: "white", overflow: "hidden" }}>
        <Grid container sx={{ minHeight: isDesktop ? "560px" : "auto" }}>

          {/* Left: accent bar + title + text */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              alignItems: "center",
              px: isDesktop ? 8 : 3,
              py: isDesktop ? 8 : 5,
            }}
          >
            <FadeInUp style={{ width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
                {/* Vertical accent bar */}
                <Box
                  sx={{
                    width: "4px",
                    minHeight: isDesktop ? "110px" : "80px",
                    backgroundColor: "#1565C0",
                    borderRadius: "2px",
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: "bold",
                      lineHeight: 1.15,
                      fontSize: isDesktop ? "2.4rem !important" : "1.8rem !important",
                      mb: 3,
                    }}
                  >
                    Psychology<br />Placement<br />Opportunities
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: "16px !important", lineHeight: 1.85, mb: 2, color: "#444" }}>
                At HealthMission Africa, we offer diverse psychology placement
                opportunities designed to match each volunteer's professional
                interests, academic background, and personal goals.
              </Typography>
              <Typography sx={{ fontSize: "16px !important", lineHeight: 1.85, mb: 2, color: "#444" }}>
                Each participant receives a personalized placement plan
                developed in collaboration with our team and local partners.
                Volunteers work alongside local professionals, contributing
                appropriately within their level of training while gaining
                exposure to the cultural and clinical realities of mental
                health care in Tanzania.
              </Typography>
              <Typography sx={{ fontSize: "16px !important", lineHeight: 1.85, color: "#444" }}>
                Depending on your academic background, interests, and the
                duration of your stay, you may intern in one or more of the
                following settings.
              </Typography>
            </FadeInUp>
          </Grid>

          {/* Right: full-bleed image */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{ position: "relative", minHeight: isDesktop ? "560px" : "300px" }}
          >
            <img
              src={Image3}
              alt="Psychology placements in Tanzania"
              style={{
                position: isDesktop ? "absolute" : "relative",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Section Header ── */}
      <Box
        sx={{
          backgroundColor: "#F8F8F8",
          pt: isDesktop ? 8 : 5,
          pb: 2,
          textAlign: "center",
        }}
      >
        <FadeInUp>
          <Container maxWidth="md">
            <Typography
              variant="overline"
              sx={{
                color: "#1565C0",
                fontWeight: "bold",
                letterSpacing: "3px",
                fontSize: "12px",
              }}
            >
              WHERE YOU MIGHT WORK
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                mt: 1,
                mb: 2,
                fontSize: isDesktop ? "2rem !important" : "1.5rem !important",
              }}
            >
              Our Placement Settings
            </Typography>
            <Typography
              sx={{
                fontSize: "17px !important",
                lineHeight: 1.8,
                color: "text.secondary",
                maxWidth: 640,
                margin: "0 auto",
              }}
            >
              Six distinct settings, each offering a unique lens on mental
              health care in Tanzania — from hospital wards to community
              workshops.
            </Typography>
          </Container>
        </FadeInUp>
      </Box>

      {/* ── Placement Cards Grid ── */}
      <Box sx={{ backgroundColor: "#F8F8F8", py: isDesktop ? 6 : 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={isDesktop ? 4 : 3} alignItems="stretch">
            {placements.map((placement, index) => (
              <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                <PlacementCard {...placement} isDesktop={isDesktop} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Apply CTA ── */}
      <Box sx={{ textAlign: "center", py: isDesktop ? 6 : 4, backgroundColor: "#F8F8F8" }}>
        <FadeInUp>
          <Typography
            sx={{
              fontSize: "17px !important",
              lineHeight: 1.8,
              mb: 3,
              color: "text.secondary",
            }}
          >
            Further details about the placements, activities, and schedules
            will be shared during the introductory call.
          </Typography>
          <Link to={`/${i18nLocale}/apply`} style={{ cursor: "pointer" }}>
            <img
              src={ApplyButton}
              alt="Apply"
              style={{
                width: isDesktop ? undefined : "140px",
                transform: applyHovered ? "scale(1.1)" : "scale(1)",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={() => setApplyHovered(true)}
              onMouseLeave={() => setApplyHovered(false)}
            />
          </Link>
        </FadeInUp>
      </Box>

      <ContactForm />
    </Box>
  );
};

export default PsychologyPlacements;
